import { forwardRef, ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import Slide, { SlideProps } from '@mui/material/Slide';
import { useReducedMotion } from 'utils/motion';

const SlideUp = forwardRef<HTMLDivElement, SlideProps>((props, ref) => (
  <Slide {...props} direction="up" ref={ref} />
));
SlideUp.displayName = 'SlideUp';

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Fires after the close transition finishes (reduced-motion: immediately).
   *  Use this to run post-close side effects (e.g. popping the route) without a
   *  hand-tuned setTimeout coupled to the animation duration. */
  onExited?: () => void;
  /** Ignore backdrop-tap dismissals so a stray tap can't discard an in-progress form;
   *  the sheet then closes only via its own Cancel/✕ control. (Escape still works.) */
  disableBackdropClose?: boolean;
  ariaLabel?: string;
}

/**
 * The single mobile bottom-sheet primitive: a MUI Dialog docked to the bottom edge that
 * slides up over a scrim. Rounded TOP corners only — the bottom is flush with the screen
 * edge. Sizes to content up to 92vh (children own their internal scroll). Reduced-motion
 * safe. Used for both the account sheet and the add-shift/add-reason flows.
 */
export const BottomSheet = ({
  open,
  onClose,
  children,
  onExited,
  disableBackdropClose = false,
  ariaLabel
}: Props) => {
  const reduced = useReducedMotion();

  const handleClose = (_event: object, reason: string) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableScrollLock
      TransitionComponent={SlideUp}
      transitionDuration={reduced ? 0 : undefined}
      TransitionProps={{ onExited }}
      aria-label={ariaLabel}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
      PaperProps={{
        sx: {
          m: 0,
          width: '100%',
          maxWidth: '100%',
          maxHeight: '92vh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {children}
    </Dialog>
  );
};
